import { FC, KeyboardEvent, useState } from 'react';
import { Button, Input } from 'reactstrap';

/**
 * Amber chip input for Batch Matches' "must-have terms" hard keyword filter
 * (README §Batch Matches control card; max 16 terms). Embedded in
 * BatchControlCard — not a shared-contract prop type (only `mustHave` /
 * `onMustHave` on BatchControlCardProps are), so its props are local to this
 * component.
 */

export interface MustHaveTermsInputProps {
  terms: string[];
  onChange: (terms: string[]) => void;
  max?: number;
}

const DEFAULT_MAX = 16;

// gold-50 / gold-200 / gold-700 (README §Batch Matches control card:
// "amber chips --gold-50/--gold-200/--gold-700").
const CHIP_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: '#FEF6E7',
  border: '1px solid #FBDBA0',
  color: '#B25E09',
  fontSize: 12,
  fontWeight: 600,
  padding: '3px 8px',
  borderRadius: 999,
  marginRight: 6,
  marginBottom: 6,
};

const REMOVE_BTN_STYLE: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#B25E09',
  cursor: 'pointer',
  fontSize: 14,
  lineHeight: 1,
  padding: 0,
};

const MustHaveTermsInput: FC<MustHaveTermsInputProps> = ({ terms, onChange, max = DEFAULT_MAX }) => {
  const [draft, setDraft] = useState('');
  const atMax = terms.length >= max;

  const addTerm = (): void => {
    const value = draft.trim();
    if (!value || atMax) return;
    if (terms.some((t) => t.toLowerCase() === value.toLowerCase())) {
      setDraft('');
      return;
    }
    onChange([...terms, value]);
    setDraft('');
  };

  const removeTerm = (term: string): void => {
    onChange(terms.filter((t) => t !== term));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTerm();
    }
  };

  return (
    <div>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#667085', display: 'block', marginBottom: 4 }}>
        Must-have terms ({terms.length}/{max})
      </label>
      {terms.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          {terms.map((term) => (
            <span key={term} style={CHIP_STYLE}>
              {term}
              <button
                type="button"
                style={REMOVE_BTN_STYLE}
                onClick={() => removeTerm(term)}
                aria-label={`Remove must-have term ${term}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <Input
          type="text"
          value={draft}
          placeholder={atMax ? 'Max 16 terms reached' : 'Add a hard keyword filter…'}
          disabled={atMax}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          style={{ height: 34, fontSize: 13 }}
        />
        <Button
          type="button"
          color="secondary"
          outline
          size="sm"
          disabled={atMax || !draft.trim()}
          onClick={addTerm}
        >
          Add
        </Button>
      </div>
    </div>
  );
};

export default MustHaveTermsInput;
